import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import {
    ListingLevel,
    ManufacturerProductBimLinkDetail,
    ManufacturerProductCatalogDetail,
    ManufacturerProductImageDetail,
    ManufacturerProductSpecDocDetail,
} from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardMedia from '@material-ui/core/CardMedia'
import Grid from '@material-ui/core/Grid'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import Truncate from 'react-truncate'
import {
    ProductListingCardAction,
    ProductListingCardActionType,
} from '../Card/ProductListingCardAction'
import { DialogContext } from '../DialogContext'

const useStyles = makeStyles((theme: Theme) => ({
    cardContainer: {
        width: 'calc(100% - 45px)',
        border: 'solid 1px #AAAAAA',
        minWidth: pxToRem(195),
        [theme.breakpoints.down('md')]: {
            minWidth: pxToRem(150),
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: pxToRem(130),
        },
        '&:hover': {
            boxShadow: '0px 0px 10px #B9EAE0',
        },
    },
    selectedCard: {
        border: '4px solid #10B091',
        boxShadow: '0px 0px 10px #B9EAE0',
    },
    customCardHeader: {
        height: 35,
        maxHeight: 35,
    },
    cardContent: {
        minHeight: pxToRem(125),
        '&.MuiCardContent-root': {
            paddingBottom: 0,
        },
    },
    productLink: {
        textDecoration: 'none',
        height: '100%',
    },
    guttered: {
        padding: theme.spacing(0.125),
    },
    grayText: {
        color: '#5E5F61',
    },
    smallerLineHeight: {
        lineHeight: 1.2,
    },
    noImage: {
        textDecoration: 'none',
        textAlign: 'center',
        height: pxToRem(80),
        [theme.breakpoints.between(1000, 1200)]: {
            height: pxToRem(60),
        },
        [theme.breakpoints.up('sm')]: {
            height: pxToRem(55),
        },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    lightGrayBackground: {
        backgroundColor: '#c9c9c9',
    },
    largeFont: {
        fontSize: pxToRem(30),
        color: '#fff',
        fontWeight: 500,
    },
    premiumNoImageTextContainer: {
        maxWidth: 308,
        fontWeight: 400,
        fontSize: pxToRem(18),
        [theme.breakpoints.between(1000, 1200)]: {
            fontSize: pxToRem(14),
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: pxToRem(14),
        },
        lineHeight: '42px',
    },
    firmLogoImage: {
        margin: theme.spacing(0.75, 0, 0, 0),
        height: 30,
        objectFit: 'contain',
        display: 'block',
        maxWidth: '100%',
    },
    proprietaryLabel: {
        height: pxToRem(12),
        width: pxToRem(50),
        fontSize: pxToRem(9),
        marginTop: theme.spacing(0.5),
        position: 'absolute',
        backgroundColor: '#EA0808',
        color: theme.palette.common.white,
        padding: theme.spacing(0, 0, 0, 0.5),
        borderRadius: '0px 2px 2px 0px',
    },
    actionContainer: { cursor: 'pointer' },
    textContentContainer: {
        height: pxToRem(30),
        maxHeight: pxToRem(30),
    },
    productNameText: {
        fontSize: pxToRem(10),
        fontWeight: 400,
        paddingLeft: theme.spacing(0.5),
    },
    logoGridItem: {
        paddingLeft: theme.spacing(1),
    },
    mediaContainer: {
        height: pxToRem(75),
        [theme.breakpoints.between(1000, 1200)]: {
            height: pxToRem(55),
        },
        [theme.breakpoints.up('sm')]: {
            height: pxToRem(50),
        },
    },
    firmNameText: {
        fontSize: pxToRem(12),
    },
    hoverText: {
        fontSize: '13px !important',
    },
}))

export interface MiniatureListingCardProps {
    firmName?: string
    firmLogoUrl?: string
    productName?: string
    hideProductName?: boolean
    productType?: string
    listingLevel?: ListingLevel
    submissionStatus?: string
    listingId?: string
    firmId?: string
    images?: Partial<ManufacturerProductImageDetail>[]
    catalogs?: Partial<ManufacturerProductCatalogDetail>[]
    manufacturerDocs?: Partial<ManufacturerProductSpecDocDetail>[]
    bimLinks?: Partial<ManufacturerProductBimLinkDetail>[]
    loading?: boolean
    isProductBOD?: boolean
    cardAction: ProductListingCardActionType
    cardClick?: (value?: number) => void
}

export interface ListingCardMedia {
    imgUrl: string
    isVideo: boolean
    order: number
}

const MiniatureListingCard: React.FC<MiniatureListingCardProps> = ({
    productName = '',
    listingId,
    firmName = '',
    productType = '',
    hideProductName = productName === 'Unknown Product Name',
    listingLevel = ListingLevel.Manufacturer,
    firmLogoUrl,
    images,
    loading,
    cardAction,
    cardClick,
}) => {
    const classes = useStyles()

    // The element anchor of the popper is the card media which has photos and videos
    const cardMediaRef = React.useRef<HTMLDivElement | null>(null)
    const { listingId: selectedListingId } = React.useContext(DialogContext)

    const consolidatedMedia: ListingCardMedia[] = React.useMemo(() => {
        const imageContents = images
            ?.filter((each) => each.filename)
            ?.map((imageItem: Partial<ManufacturerProductImageDetail>) => ({
                imgUrl: imageItem.filename!,
                isVideo: false,
                order: imageItem.order ?? 0,
            }))
            .sort((a, b) => a.order - b.order)
        return imageContents && imageContents?.length! > 0
            ? [[...imageContents]?.[0]]
            : []
    }, [images])

    const listingPath = `/products/${
        listingId ? 'product/listing/' + listingId : 'all'
    }`

    const isAction = cardAction === 'Action'
    const handleCardClick = isAction ? () => cardClick?.() : undefined

    return (
        <HoverTooltip
            text={
                <>
                    <Typography className={classes.hoverText}>
                        {firmName}
                    </Typography>
                    <Typography className={classes.hoverText}>
                        {productType}
                    </Typography>
                    <Typography className={classes.hoverText}>
                        {productName}
                    </Typography>
                </>
            }
            placements={['top', 'right']}
        >
            <Card
                variant="elevation"
                square
                elevation={2}
                className={clsx(
                    'productListingCardItem',
                    classes.cardContainer,
                    listingId &&
                        listingId !== '' &&
                        listingId === selectedListingId &&
                        classes.selectedCard
                )}
                data-testid={`productListingCardItem${listingId}`}
                onKeyDown={onEnterKeyDown(handleCardClick)}
            >
                <CardContent
                    component={Grid}
                    container
                    direction="column"
                    spacing={1}
                    className={clsx(
                        'cardBodyContainer',
                        classes.guttered,
                        classes.cardContent
                    )}
                    onClick={handleCardClick}
                >
                    <GridContainer
                        justifyContent="space-between"
                        alignItems="center"
                        className={clsx(
                            classes.customCardHeader,
                            classes.guttered,
                            cardAction === 'Action' && classes.actionContainer
                        )}
                        onClick={handleCardClick}
                    >
                        <GridItem className={classes.logoGridItem}>
                            {firmLogoUrl ? (
                                <img
                                    src={firmLogoUrl}
                                    className={classes.firmLogoImage}
                                    alt={firmName}
                                    title={firmName}
                                />
                            ) : (
                                <Typography
                                    className={classes.firmNameText}
                                    component="span"
                                >
                                    {firmName}
                                </Typography>
                            )}
                        </GridItem>
                    </GridContainer>
                    <GridItem>
                        {processEnv.REACT_APP_EnableBadgelessCards !== '1' && (
                            <span className={classes.proprietaryLabel}>
                                Proprietary
                            </span>
                        )}
                        {consolidatedMedia.length > 0 ? (
                            <CardMedia
                                ref={cardMediaRef}
                                image={consolidatedMedia?.[0]?.imgUrl}
                                className={classes.mediaContainer}
                            />
                        ) : (
                            <ProductListingCardAction
                                type={cardAction}
                                to={listingPath}
                                linkClassName={clsx(
                                    classes.noImage,
                                    classes.lightGrayBackground
                                )}
                                actionClassName={clsx(
                                    classes.noImage,
                                    classes.lightGrayBackground
                                )}
                                onClick={() => cardClick?.()}
                                unselectable="on"
                            >
                                <Typography
                                    className={clsx(
                                        classes.largeFont,
                                        classes.premiumNoImageTextContainer,
                                        'productListingCardNoImageText'
                                    )}
                                    component="span"
                                >
                                    {listingLevel}
                                </Typography>
                            </ProductListingCardAction>
                        )}
                    </GridItem>
                    <GridItem className={classes.textContentContainer}>
                        <ProductListingCardAction
                            type={cardAction}
                            to={listingPath}
                            linkClassName={classes.productLink}
                        >
                            {!loading ? (
                                <>
                                    {productName && !hideProductName && (
                                        <GridItem>
                                            <Typography
                                                variant="h6"
                                                className={clsx(
                                                    'productListingCardProductName',
                                                    classes.smallerLineHeight,
                                                    classes.grayText,
                                                    classes.productNameText
                                                )}
                                                component="span"
                                            >
                                                <Truncate lines={2}>
                                                    {productName}
                                                </Truncate>
                                            </Typography>
                                        </GridItem>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/* Product Name */}
                                    <Skeleton
                                        variant="text"
                                        animation="pulse"
                                        width={70}
                                        className="loadingSkeleton"
                                    />
                                </>
                            )}
                        </ProductListingCardAction>
                    </GridItem>
                </CardContent>
            </Card>
        </HoverTooltip>
    )
}

export default MiniatureListingCard
